import type { OpportunityEntry as ApiOpportunityEntry, Product as ApiProduct, Reseller as ApiReseller } from "randmar-api-client";
import { Product } from "../product/Product";
import Reseller from "../reseller/Reseller";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export class Opportunity {
  /** The unique identifier for the bid. */
  public bidNumber?: string;
  /** The name or title of the opportunity. */
  public opportunity?: string;
  /** A short description of the opportunity. */
  public description?: string;
  /** The start date of the opportunity, in string format. */
  public startDate?: string;
  /** The end date of the opportunity, in string format. */
  public endDate?: string;
  /** The date of the last update, in string format. */
  public lastUpdate?: string;
  /** A boolean indicating if the opportunity is active. */
  public active?: boolean;
  /** A string containing default resellers. */
  public defaultResellers?: string;
  /** A boolean indicating if this opportunity can be set as a default. */
  public canBeSetAsDefault?: boolean;
  /** The price for the bid. */
  public bidPrice?: number;
  /** The type of the bid price (e.g., 'Rebate', 'SpecialNet'). */
  public bidPriceType?: string;
  /** The Randmar SKU if the opportunity is for a single product. */
  public randmarSKU?: string;

  /** A list of resellers associated with this opportunity. */
  public resellers?: Reseller[];
  /** A list of products and pricing details for this opportunity. */
  public details?: OpportunityDetail[];

  public static convertFrom(source: ApiOpportunityEntry | unknown): Opportunity {
    const opportunity = new Opportunity();

    if (isRecord(source)) {
      opportunity.bidNumber = typeof source.BidNumber === "string" ? source.BidNumber : undefined;
      opportunity.opportunity = typeof source.Opportunity === "string" ? source.Opportunity : undefined;
      opportunity.startDate = typeof source.StartDate === "string" ? source.StartDate : undefined;
      opportunity.endDate = typeof source.EndDate === "string" ? source.EndDate : undefined;
      opportunity.lastUpdate = typeof source.LastUpdate === "string" ? source.LastUpdate : undefined;
      opportunity.active = typeof source.Active === "boolean" ? source.Active : undefined;
      opportunity.defaultResellers = typeof source.DefaultResellers === "string" ? source.DefaultResellers : undefined;
      opportunity.description = typeof source.Description === "string" ? source.Description : undefined;
      opportunity.canBeSetAsDefault = typeof source.CanBeSetAsDefault === "boolean" ? source.CanBeSetAsDefault : undefined;
      opportunity.bidPrice = typeof source.BidPrice === "number" ? source.BidPrice : undefined;
      opportunity.bidPriceType = typeof source.BidPriceType === "string" ? source.BidPriceType : undefined;
      opportunity.randmarSKU = typeof source.RandmarSKU === "string" ? source.RandmarSKU : undefined;

      const resellers = source.Resellers;
      if (Array.isArray(resellers)) {
        opportunity.resellers = resellers
          .filter((x): x is ApiReseller => isRecord(x))
          .map((x) => Reseller.convertFrom(x as ApiReseller));
      }

      const details = source.Details;
      if (Array.isArray(details)) {
        opportunity.details = details.map(OpportunityDetail.convertFrom);
      }
    } else {
      const entry = source as ApiOpportunityEntry;
      opportunity.bidNumber = entry.BidNumber ?? undefined;
      opportunity.opportunity = (entry.Opportunity ?? entry.OpportunityName) ?? undefined;
      opportunity.description = entry.Description ?? undefined;
      opportunity.startDate = entry.StartDate ?? undefined;
      opportunity.endDate = entry.EndDate ?? undefined;
      opportunity.lastUpdate = entry.LastUpdate ?? undefined;
      opportunity.active = entry.Active ?? undefined;
      opportunity.defaultResellers = entry.DefaultResellers ?? undefined;
      opportunity.canBeSetAsDefault = entry.CanBeSetAsDefault ?? undefined;
      opportunity.bidPrice = entry.BidPrice ?? undefined;
      opportunity.bidPriceType = entry.BidPriceType ?? undefined;
      opportunity.randmarSKU = entry.RandmarSKU ?? undefined;
    }

    return opportunity;
  }
}

export class OpportunityDetail {
  /** The unique identifier for the bid. */
  public bidNumber?: string;
  /** The bid price for this specific product. */
  public bidPrice?: number;
  /** The type of bid price for this product. */
  public bidPriceType?: string;
  /** Whether this detail line is discounted by Randmar and not claimed to the manufacturer. */
  public randmarDiscount?: boolean;
  /** The Randmar SKU for the product in this detail line. */
  public randmarSKU?: string;

  /** The product associated with this detail line. */
  public product?: Product;

  public static convertFrom(source: unknown): OpportunityDetail {
    const opportunityDetail = new OpportunityDetail();

    if (!isRecord(source)) {
      return opportunityDetail;
    }

    opportunityDetail.bidNumber = typeof source.BidNumber === "string" ? source.BidNumber : undefined;
    opportunityDetail.bidPrice = typeof source.BidPrice === "number" ? source.BidPrice : undefined;
    opportunityDetail.bidPriceType = typeof source.BidPriceType === "string" ? source.BidPriceType : undefined;
    opportunityDetail.randmarDiscount = typeof source.RandmarDiscount === "boolean" ? source.RandmarDiscount : undefined;
    opportunityDetail.randmarSKU = typeof source.RandmarSKU === "string" ? source.RandmarSKU : undefined;

    const product = source.Product;
    if (product && isRecord(product)) {
      opportunityDetail.product = Product.convertFromNewProduct(product as ApiProduct);
    }

    return opportunityDetail;
  }
}
